import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageWindowStart: number;
  onPageWindowStartChange: (start: number) => void;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  pageWindowStart,
  onPageWindowStartChange,
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const PAGE_WINDOW = 10; // 一度に表示するページ数
  const PAGE_STEP = 5; // ← / → で移動するページ数

  // ウィンドウの終端（totalPages を超えないよう調整）
  const pageWindowEnd = Math.min(pageWindowStart + PAGE_WINDOW - 1, totalPages);

  const canGoBack = pageWindowStart > 1;
  const canGoForward = pageWindowEnd < totalPages;

  const shiftBack = () => {
    const next = Math.max(1, pageWindowStart - PAGE_STEP);
    onPageWindowStartChange(next);
  };

  const shiftForward = () => {
    const next = Math.min(
      totalPages - PAGE_WINDOW + 1,
      pageWindowStart + PAGE_STEP
    );
    onPageWindowStartChange(Math.max(1, next));
  };

  const visiblePages = Array.from(
    { length: pageWindowEnd - pageWindowStart + 1 },
    (_, i) => pageWindowStart + i
  );

  const paginationStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '6px',
    marginTop: '30px',
    flexWrap: 'wrap',
  };

  const arrowBtnStyle = (enabled: boolean): React.CSSProperties => ({
    padding: '8px 14px',
    borderRadius: '8px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid rgba(231, 228, 223, 0.1)',
    color: 'var(--color-text)',
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.35,
    fontWeight: 'bold',
    fontSize: '15px',
    transition: 'opacity 0.2s',
  });

  const pageBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 13px',
    borderRadius: '8px',
    backgroundColor: active ? 'var(--color-primary)' : 'var(--color-surface)',
    border: '1px solid rgba(231, 228, 223, 0.1)',
    color: 'var(--color-text)',
    fontWeight: active ? 'bold' : 'normal',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'background-color 0.15s',
    boxShadow: active ? '0 0 10px rgba(107, 78, 255, 0.35)' : 'none',
  });

  return (
    <div style={paginationStyle}>
      {/* ← ウィンドウを5ページ戻す */}
      <button
        type="button"
        disabled={!canGoBack}
        onClick={shiftBack}
        style={arrowBtnStyle(canGoBack)}
        title="5ページ前へ"
      >
        ←
      </button>

      {/* ページ番号ボタン */}
      {visiblePages.map((page) => (
        <button
          key={page}
          type="button"
          onClick={() => onPageChange(page)}
          style={pageBtnStyle(currentPage === page)}
        >
          {page}
        </button>
      ))}

      {/* → ウィンドウを5ページ進める */}
      <button
        type="button"
        disabled={!canGoForward}
        onClick={shiftForward}
        style={arrowBtnStyle(canGoForward)}
        title="5ページ先へ"
      >
        →
      </button>
    </div>
  );
}
